import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import SyncIcon from '@mui/icons-material/Sync';
import apiService from '../../services/api.service';
import { SoaAgingBasis, SoaCardType, SoaConfig, SoaPartner, SoaTemplateDefinition } from '../../types/formattedSoa';
import { printPdfBlob, saveBlob, soaBlobErrorMessage, soaErrorMessage } from '../../utils/formattedSoa';
import SoaPreviewFrame from './PreviewFrame';

/**
 * The designer's preview: the unsaved template printed for a chosen customer or supplier (read from SAP),
 * or for sample data when none is chosen. It follows each change after a short pause. PDF and Print use
 * the server's PDF, laid out exactly like the preview.
 */

interface Props {
  companyId: number;
  definition: SoaTemplateDefinition;
  config: SoaConfig;
  height?: string | number;
}

const DesignerPreview: React.FC<Props> = ({ companyId, definition, config, height = '70vh' }) => {
  const [cardType, setCardType] = useState<SoaCardType>('C');
  const [partner, setPartner] = useState<SoaPartner | null>(null);
  const [fromDate, setFromDate] = useState(config.defaults.fromDate);
  const [toDate, setToDate] = useState(config.defaults.toDate);
  // The ageing basis, as Customer SOA: the open items' age from the invoice (posting) date or the due date.
  const [basis, setBasis] = useState<SoaAgingBasis>('invoice');
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'pdf' | 'print' | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const forceRefresh = useRef(false);
  const request = useRef(0);

  // Partner search (debounced).
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SoaPartner[]>([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      setSearching(true);
      apiService.getSoaPartners(companyId, cardType, search)
        .then((res: any) => { if (!cancelled) setOptions(Array.isArray(res?.data) ? res.data : []); })
        .catch(() => { if (!cancelled) setOptions([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [companyId, cardType, search]);

  const datesOk = /^\d{4}-\d{2}-\d{2}$/.test(fromDate) && /^\d{4}-\d{2}-\d{2}$/.test(toDate) && fromDate <= toDate;

  // The preview follows the template, the partner and the dates after a pause.
  useEffect(() => {
    if (!companyId || !datesOk) return undefined;
    const id = request.current + 1;
    request.current = id;
    const t = setTimeout(() => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      setLoading(true);
      apiService.previewSoa({
        companyId, definition, cardType, cardCodes: partner ? [partner.cardCode] : [], fromDate, toDate, basis, sample: !partner, refresh,
      })
        .then((res: any) => {
          if (request.current !== id) return;
          setHtml(res?.data?.html ?? null);
          setError(null);
        })
        .catch((e: any) => { if (request.current === id) setError(soaErrorMessage(e, 'The preview could not be prepared')); })
        .finally(() => { if (request.current === id) setLoading(false); });
    }, 800);
    return () => clearTimeout(t);
  }, [companyId, definition, cardType, partner, fromDate, toDate, basis, datesOk, refreshTick]);

  const pdf = async (mode: 'pdf' | 'print') => {
    setBusy(mode);
    setNotes([]);
    try {
      const out = await apiService.downloadSoaPdf({
        companyId, definition, cardType, cardCodes: partner ? [partner.cardCode] : [], fromDate, toDate, basis, sample: !partner,
      });
      setNotes(out.notes);
      if (mode === 'pdf') saveBlob(out.blob, out.fileName);
      else printPdfBlob(out.blob);
    } catch (e: any) {
      setError(await soaBlobErrorMessage(e, 'The PDF could not be prepared'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={cardType} aria-label="Customer or supplier"
          onChange={(_, v: SoaCardType | null) => { if (v) { setCardType(v); setPartner(null); setOptions([]); } }}>
          <ToggleButton value="C">Customer</ToggleButton>
          <ToggleButton value="S" disabled={!config.permissions.suppliers}>Supplier</ToggleButton>
        </ToggleButtonGroup>
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 220 }}
          options={options}
          loading={searching}
          value={partner}
          filterOptions={(x) => x}
          isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
          getOptionLabel={(o) => `${o.cardCode} — ${o.cardName}`}
          onChange={(_, v) => setPartner(v)}
          onInputChange={(_, v, reason) => { if (reason === 'input') setSearch(v); if (reason === 'clear') setSearch(''); }}
          renderInput={(p) => (
            <TextField
              {...p}
              label={cardType === 'S' ? 'Supplier (empty = sample data)' : 'Customer (empty = sample data)'}
              InputProps={{ ...p.InputProps, endAdornment: <>{searching ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</> }}
            />
          )}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <TextField size="small" type="date" label="From" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField size="small" type="date" label="To" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <ToggleButtonGroup size="small" exclusive value={basis} aria-label="Age the statement by"
          onChange={(_, v: SoaAgingBasis | null) => { if (v) setBasis(v); }}>
          <ToggleButton value="invoice" aria-label="Age by invoice date">Invoice date</ToggleButton>
          <ToggleButton value="due" aria-label="Age by due date">Due date</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title={partner ? 'Read the journal lines from SAP again' : 'Sample data: nothing is read from SAP'}>
          <span>
            <Button size="small" startIcon={<SyncIcon />} disabled={!partner || loading}
              onClick={() => { forceRefresh.current = true; setRefreshTick((n) => n + 1); }}>
              Refresh SAP data
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={busy === 'pdf' ? <CircularProgress size={14} /> : <PictureAsPdfIcon />} disabled={!!busy || !datesOk} onClick={() => pdf('pdf')}>PDF</Button>
        <Button size="small" variant="outlined" startIcon={busy === 'print' ? <CircularProgress size={14} /> : <PrintIcon />} disabled={!!busy || !datesOk} onClick={() => pdf('print')}>Print</Button>
      </Stack>
      {!datesOk && <Alert severity="warning" sx={{ mb: 1 }}>The From date must be on or before the To date.</Alert>}
      {notes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setNotes([])}>
          {notes.map((n) => <div key={n}>{n}</div>)}
        </Alert>
      )}
      <SoaPreviewFrame html={html} loading={loading} error={error} height={height}
        emptyText={loading ? 'Preparing the preview…' : 'The preview appears here.'} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {partner ? `${partner.cardName}: journal lines read from SAP (kept 3 minutes; Refresh to read again).` : 'Sample data (no SAP read). Choose a business partner to see real figures.'}
      </Typography>
    </Box>
  );
};

export default DesignerPreview;
